/**
 * useDraftingPubSub — owns the PubSub lifecycle for a single drafting item.
 *
 * Join a hub group, filter events by correlationId,
 * dispatch applyPubSubEvent for status
 * + progress, and refetch the item on terminal events. The hook is idempotent
 * — calling join() with the same (group, correlationId) is a no-op.
 *
 * Cleanup on unmount leaves the group, unregisters the handler, and clears
 * the 30s safety re-fetch timer.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector, type RootState } from '../store';
import { getMultiHubSocketService } from '../utils/MultiHubSocketService';
import {
  applyPubSubEvent,
  fetchDraftingItem,
  type DraftingKind,
} from '../store/slices/draftingSlice';

const HUB = 'ai_processing';
// Backstop refetch if the socket goes quiet. Kept short: the primary recovery
// is now the catch-up fetch fired right after join (see below), so this only
// covers a mid-generation stall, not the initial subscribe race.
const SAFETY_REFETCH_MS = 10_000;

interface UseDraftingPubSubResult {
  join: (group: string, correlationId: string) => Promise<void>;
  leave: () => void;
}

export function useDraftingPubSub(
  kind: DraftingKind | undefined,
  uuid: string | undefined,
): UseDraftingPubSubResult {
  const dispatch = useAppDispatch();
  // Read userId from the store so we can defensively seed the multi-hub
  // singleton — the Sidebar normally does this via getNotificationsService,
  // but routes that mount before / without the Sidebar would otherwise see
  // a silent negotiate-skipped warning.
  const userId = useAppSelector((s: RootState) => s.user?.userInfo?.UserID);

  const groupRef = useRef<string | null>(null);
  const correlationIdRef = useRef<string | null>(null);
  const handlerRef = useRef<((...args: any[]) => void) | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetSafetyTimer = useCallback(() => {
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    if (!kind || !uuid) return;
    safetyTimerRef.current = setTimeout(() => {
      dispatch(fetchDraftingItem({ kind, uuid }));
    }, SAFETY_REFETCH_MS);
  }, [dispatch, kind, uuid]);

  const leave = useCallback(() => {
    const hub = getMultiHubSocketService();
    const group = groupRef.current;
    const handler = handlerRef.current;
    if (handler) hub.off(HUB, '*', handler);
    if (group) hub.emit(HUB, 'leave', { group });
    groupRef.current = null;
    correlationIdRef.current = null;
    handlerRef.current = null;
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  }, []);

  const join = useCallback(
    async (group: string, correlationId: string) => {
      // Idempotent — same join is a no-op.
      if (groupRef.current === group && correlationIdRef.current === correlationId) {
        return;
      }
      // Switched groups — tear down the old one first.
      if (groupRef.current && groupRef.current !== group) {
        leave();
      }

      const hub = getMultiHubSocketService();
      // Defensive: seed the singleton's currentUserId in case nothing else
      // (Sidebar / notifications service) has done it yet for this session.
      if (userId) {
        hub.setCurrentUserId(String(userId));
      }
      await hub.connectToHub(HUB);

      // Record identity BEFORE wiring the listener so the correlationId filter
      // matches the very first event that arrives.
      groupRef.current = group;
      correlationIdRef.current = correlationId;

      // Register the listener BEFORE emitting join. Previously the join was
      // emitted first and the handler attached afterwards, so any event the
      // backend published in that window was dropped — leaving the UI stuck at
      // 'queued' until a manual refresh. Handler-first closes that race.
      if (!handlerRef.current) {
        const handler = (data: any) => {
          const eventData = Array.isArray(data) ? data[0] : (data || {});
          const nested = eventData.data || {};
          const eventCorrelationId =
            eventData.correlationId || eventData.correlation_id || eventData.CorrelationID;
          if (!eventCorrelationId || eventCorrelationId !== correlationIdRef.current) return;

          const status = (eventData.status || nested.status || '').toLowerCase();
          const sub_type = eventData.sub_type || nested.sub_type || eventData.event_name || '';
          const message = eventData.message || eventData.stepName || '';
          const progress_percent =
            eventData.progressPercent ?? eventData.progress_percent ?? eventData.progress ?? null;

          dispatch(applyPubSubEvent({
            correlationId: eventCorrelationId,
            status,
            sub_type,
            progress_percent,
            message,
          }));
          resetSafetyTimer();

          const isTerminal = status === 'completed' || status === 'failed'
            || sub_type === 'draft_completed' || sub_type === 'draft_failed';
          if (isTerminal && kind && uuid) {
            // Refetch the item so word_count / updated_at / name etc. reflect
            // the post-generation truth. The stream-open effect in the
            // consumer reacts to status flipping to 'completed'.
            dispatch(fetchDraftingItem({ kind, uuid }));
            leave();
          }
        };
        handlerRef.current = handler;
        hub.on(HUB, '*', handler);
      }

      // Now join the group.
      hub.emit(HUB, 'join', { group });

      // Catch-up reconcile: the socket connect + server-side group join are
      // async, so the backend may have already published progress before our
      // membership took effect. Pull the current row from the DB once so we
      // never start from a stale 'queued' snapshot even if an early event was
      // published before the group join landed.
      if (kind && uuid) {
        dispatch(fetchDraftingItem({ kind, uuid }));
      }

      resetSafetyTimer();
    },
    [dispatch, kind, uuid, leave, resetSafetyTimer, userId],
  );

  // Cleanup on unmount or when (kind, uuid) changes — caller is expected to
  // call join() again for the new item.
  useEffect(() => {
    return () => {
      leave();
    };
  }, [kind, uuid, leave]);

  // Stable reference so consumers can safely list the returned object in
  // effect deps without re-running every render (the old inline object
  // identity changed each render, re-firing the join effect continuously).
  return useMemo(() => ({ join, leave }), [join, leave]);
}
